import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { 
  getSentProfileViewRequestsAction, 
  clearSentProfileViewRequestsAction
} from "../../redux/actions/userActions";
import { withdrawProfileViewRequestAction } from "../../redux/actions/profileViewRequestActions";
import { useToast } from "../../contexts/ToastContext";
import CommonLoading from "../loader/CommonLoading";
import LoadMoreButton from "../shared/LoadMoreButton";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import CountdownTimer from "../shared/CountdownTimer";

const SentProfileViewRequestsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [withdrawingRequestId, setWithdrawingRequestId] = useState(null);
  const sortOrder = "desc"; // Fixed sort order (newest first)
  
  const sentRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);
  const totalSentRequests = useSelector((state) => state.user?.totalSentProfileViewRequests || 0);

  const LIMIT = 25;

  useEffect(() => {
    if (isOpen) {
      const fetchRequests = async () => {
        setLoading(true);
        await dispatch(getSentProfileViewRequestsAction(null, LIMIT, 0));
        setLoading(false);
      };

      fetchRequests();
    }

    return () => {
      if (!isOpen) {
        dispatch(clearSentProfileViewRequestsAction());
      }
    };
  }, [dispatch, LIMIT, isOpen]);

  const handleUserClick = useCallback((user) => {
    const profileUrl = user?.profileUrl || user?._id;
    if (profileUrl) {
      onClose();
      navigate(`/profile/${profileUrl}`);
    }
  }, [navigate, onClose]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const currentLength = sentRequests?.length || 0;
    dispatch(getSentProfileViewRequestsAction(null, LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, sentRequests?.length]);

  const handleWithdraw = useCallback(async (requestId) => {
    setWithdrawingRequestId(requestId);
    try {
      const result = await dispatch(withdrawProfileViewRequestAction(requestId));
      if (result?.success) {
        showSuccess("Profile view request withdrawn successfully");
        // Refresh the list to update counts
        await dispatch(getSentProfileViewRequestsAction(null, LIMIT, 0));
      } else {
        showError(result?.error || "Failed to withdraw request");
      }
    } catch (error) {
      showError("Error withdrawing request");
    } finally {
      setWithdrawingRequestId(null);
    }
  }, [dispatch, showSuccess, showError, LIMIT]);

  // Sort requests by newest/oldest
  const sortedRequests = useMemo(() => {
    if (!sentRequests || sentRequests.length === 0) return [];
    
    const sorted = [...sentRequests].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
    
    return sorted;
  }, [sentRequests, sortOrder]);


  const displayedRequests = useMemo(() => {
    return sortedRequests.map((request) => {
      const profileOwner = request.profileOwner;
      if (!profileOwner) return null;

      return (
        <div 
          key={request._id}
          className="bg-white border rounded-md w-full shadow-2xl shadow-[#f2f5fc] relative hover:shadow-lg hover:border-primary-300 transition-all duration-300"
        >
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="hover:bg-gray-50 rounded-md p-3 -m-3 transition-colors">
              <div className="flex items-start justify-between gap-3">
                {/* Left: User info with times below name */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <img
                      src={profileOwner.avatar || "/default-avatar.png"}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div 
                        className="cursor-pointer mb-1"
                        onClick={() => handleUserClick(profileOwner)}
                      >
                        <h4 className="font-semibold text-gray-800 font-heading text-base truncate">
                          {profileOwner.name}
                        </h4>
                      </div>
                      
                      {/* Times directly below name - aligned */}
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-600 font-medium font-flex-regular">
                          Sent: {new Date(request.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {request.status === "accepted" &&
                          profileOwner?.privacySettings?.accessType === "expiry" &&
                          profileOwner?.privacySettings?.expiryValue &&
                          profileOwner?.privacySettings?.expiryUnit &&
                          request.updatedAt && (() => {
                            // Calculate expiry date
                            const acceptedDate = new Date(request.updatedAt);
                            let milliseconds = 0;
                            switch (profileOwner.privacySettings.expiryUnit) {
                              case "hours": milliseconds = profileOwner.privacySettings.expiryValue * 60 * 60 * 1000; break;
                              case "days": milliseconds = profileOwner.privacySettings.expiryValue * 24 * 60 * 60 * 1000; break;
                              case "weeks": milliseconds = profileOwner.privacySettings.expiryValue * 7 * 24 * 60 * 60 * 1000; break;
                              case "years": milliseconds = profileOwner.privacySettings.expiryValue * 365 * 24 * 60 * 60 * 1000; break;
                              default: milliseconds = 0; break;
                            }
                            const expiryDate = new Date(acceptedDate.getTime() + milliseconds);
                            const isExpired = new Date() > expiryDate;
                            
                            return (
                              <span className="text-xs text-gray-600 font-medium font-flex-regular">
                                {isExpired ? "Expired: " : "Expires: "}
                                {expiryDate.toLocaleString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            );
                          })()}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right: Status badges - stacked vertically */}
                <div className="flex-shrink-0 flex flex-col items-end gap-2">
                  {request.status === "pending" && (
                    <>
                      <span className="px-3 py-1 text-xs font-semibold text-yellow-700 bg-yellow-100 rounded-full whitespace-nowrap">
                        Pending
                      </span>
                      <button
                        onClick={() => handleWithdraw(request._id)}
                        disabled={withdrawingRequestId === request._id}
                        className="px-3 py-1 text-xs font-medium text-action bg-action/10 hover:bg-action/20 rounded-full whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {withdrawingRequestId === request._id ? "Withdrawing..." : "Withdraw"}
                      </button>
                    </>
                  )}
                  {request.status === "accepted" && (() => {
                    // Check if access has expired
                    let isExpired = false;
                    if (profileOwner?.privacySettings?.accessType === "expiry" &&
                        profileOwner?.privacySettings?.expiryValue &&
                        profileOwner?.privacySettings?.expiryUnit &&
                        request.updatedAt) {
                      const acceptedDate = new Date(request.updatedAt);
                      let milliseconds = 0;
                      switch (profileOwner.privacySettings.expiryUnit) {
                        case "hours": milliseconds = profileOwner.privacySettings.expiryValue * 60 * 60 * 1000; break;
                        case "days": milliseconds = profileOwner.privacySettings.expiryValue * 24 * 60 * 60 * 1000; break;
                        case "weeks": milliseconds = profileOwner.privacySettings.expiryValue * 7 * 24 * 60 * 60 * 1000; break;
                        case "years": milliseconds = profileOwner.privacySettings.expiryValue * 365 * 24 * 60 * 60 * 1000; break;
                        default: milliseconds = 0; break;
                      }
                      const expiryDate = new Date(acceptedDate.getTime() + milliseconds);
                      isExpired = new Date() > expiryDate;
                    }

                    return (
                      <div className="flex flex-col items-end gap-1.5">
                        {/* Top: Accepted badge */}
                        <span className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full whitespace-nowrap">
                          Accepted
                        </span>
                        {/* Bottom: Expiry countdown */}
                        {profileOwner?.privacySettings?.accessType === "expiry" &&
                          profileOwner?.privacySettings?.expiryValue &&
                          profileOwner?.privacySettings?.expiryUnit &&
                          !isExpired && (
                          <div className="flex items-center justify-end">
                            <CountdownTimer
                              acceptedAt={request.updatedAt}
                              expiryValue={profileOwner.privacySettings.expiryValue}
                              expiryUnit={profileOwner.privacySettings.expiryUnit}
                            />
                          </div>
                        )}
                        {/* Access Expired badge - shown at bottom when expired */}
                        {isExpired && (
                          <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
                            Access Expired
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {request.status === "denied" && (
                    <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
                      Denied
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }).filter(Boolean);
  }, [sortedRequests, handleUserClick, handleWithdraw, withdrawingRequestId]);

  const shouldShowLoadMore = useMemo(() => {
    if (!sentRequests || sentRequests.length === 0) return false;
    const currentCount = sentRequests?.length || 0;
    const totalCount = totalSentRequests || 0;
    return currentCount < totalCount && totalCount > LIMIT;
  }, [sentRequests, totalSentRequests, LIMIT]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-4">
                  <div className="flex items-center gap-3">
                    <FaPaperPlane className="text-2xl text-primary-600" />
                    <Dialog.Title className="text-lg font-semibold text-gray-700">
                      Sent Profile View Requests
                    </Dialog.Title>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FaTimes className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                  {loading ? (
                    <div className="flex justify-center items-center py-8">
                      <CommonLoading />
                    </div>
                  ) : sentRequests?.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 gap-3">
                        {displayedRequests}
                      </div>
                      
                      {shouldShowLoadMore && (
                        <div className="mt-4">
                          <LoadMoreButton
                            onClick={handleLoadMore}
                            isLoading={isLoadMoreLoading}
                            text="Load More Requests"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <div className="flex justify-center items-center flex-col">
                        <FaPaperPlane className="text-6xl text-gray-300 mb-4" />
                        <p className="text-gray-500 py-4 font-roboto-flex">
                          You haven't sent any profile view requests yet.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SentProfileViewRequestsModal;
